/**
 * 版本管理工具
 */

// 版本類型常數
export const VERSION_TYPES = {
	APP_VERSION_IOS: 'APP_VERSION_IOS',
	APP_VERSION_ANDROID: 'APP_VERSION_ANDROID',
	BACKSTAGE_H5_VERSION: 'BACKSTAGE_H5_VERSION',
}

/**
 * 檢查是否為 iOS 設備（包含 Mac）
 * @returns {boolean} 是否為 iOS 設備
 */
export const isIos = () => {
	return /iPhone|iPad|iPod|Mac|macintosh|mac os x/i.test(navigator.userAgent)
}

/**
 * 檢查是否為 Android 系統
 * @returns {boolean} 是否為 Android 系統
 */
export const isAndroid = () => {
	return /Android/i.test(navigator.userAgent)
}

/**
 * 檢查是否為 React Native 環境
 * @returns {boolean} 是否為 React Native 環境
 */
export const isApp = () => {
	return !!window.ReactNativeWebView?.postMessage
}

/**
 * 檢查是否為 iOS React Native App
 * @returns {boolean} 是否為 iOS React Native App
 */
export const isIosApp = () => {
	return isApp() && isIos()
}

/**
 * 檢查是否為 Android React Native App
 * @returns {boolean} 是否為 Android React Native App
 */
export const isAndroidApp = () => {
	return isApp() && isAndroid()
}

/**
 * 獲取當前 App 版本號
 * @param {object} store - Vuex store 實例
 * @returns {string} 當前 App 版本號
 */
export const getCurrentAppVersion = store => {
	if (!store || !store.state) return ''

	if (isIosApp()) {
		return store.state.storage?.iosVersion || ''
	} else if (isAndroidApp()) {
		return store.state.storage?.androidVersion || ''
	}
	return ''
}

/**
 * 獲取當前後台版本號（從環境變數取得）
 * @returns {string} 當前後台版本號
 */
export const getCurrentBackstageVersion = () => {
	return process.env.VUE_APP_VERSION || ''
}

/**
 * 獲取當前 iOS App 版本號
 * @param {object} store - Vuex store 實例
 * @returns {string} 當前 iOS App 版本號
 */
export const getCurrentIosVersion = store => {
	if (!store || !store.state) return ''
	return store.state.storage?.iosVersion || ''
}

/**
 * 獲取當前 Android App 版本號
 * @param {object} store - Vuex store 實例
 * @returns {string} 當前 Android App 版本號
 */
export const getCurrentAndroidVersion = store => {
	if (!store || !store.state) return ''
	return store.state.storage?.androidVersion || ''
}

/**
 * 根據當前平台獲取對應的 App 下載連結
 * @param {string} iosAppUrl - iOS App 下載連結
 * @param {string} androidAppUrl - Android App 下載連結
 * @returns {string} 對應平台的下載連結，如果不是 App 環境或無對應連結則返回空字串
 */
export const getAppDownloadUrl = (iosAppUrl, androidAppUrl) => {
	if (!isApp()) {
		return ''
	}

	if (isIos()) {
		return iosAppUrl || ''
	} else if (isAndroid()) {
		return androidAppUrl || ''
	}

	return ''
}

/**
 * 檢查是否有新版本可用
 * @param {string} currentVersion - 當前版本
 * @param {string} latestVersion - 最新版本
 * @returns {boolean} 是否有新版本
 */
export const hasNewVersion = (currentVersion, latestVersion) => {
	return compareVersions(currentVersion, latestVersion) < 0
}

/**
 * 比較版本號大小（語義化版本）
 * @param {string} version1 - 版本1
 * @param {string} version2 - 版本2
 * @returns {number} -1: version1 < version2, 0: 相等, 1: version1 > version2
 */
const compareVersions = (version1, version2) => {
	if (!version1 || !version2) return 0

	// 清理版本號格式
	const cleanV1 = cleanVersion(version1)
	const cleanV2 = cleanVersion(version2)

	const v1parts = cleanV1.split('.').map(Number)
	const v2parts = cleanV2.split('.').map(Number)
	const maxLength = Math.max(v1parts.length, v2parts.length)

	for (let i = 0; i < maxLength; i++) {
		const v1part = v1parts[i] || 0
		const v2part = v2parts[i] || 0

		if (v1part < v2part) return -1
		if (v1part > v2part) return 1
	}

	return 0
}

/**
 * 清理版本號格式（移除 v 前綴）
 * @param {string} version - 版本號
 * @returns {string} 清理後的版本號
 */
const cleanVersion = version => {
	if (!version) return ''
	return version.replace(/^v/, '')
}

export default {
	VERSION_TYPES,
	isApp,
	isIos,
	isAndroid,
	isIosApp,
	isAndroidApp,

	getCurrentAppVersion,
	getCurrentBackstageVersion,
	getCurrentIosVersion,
	getCurrentAndroidVersion,
	getAppDownloadUrl,

	hasNewVersion,
}
